/**
 * Visual Artifact Workflow Actions
 *
 * Pure data helpers for actions that can be shown beside visual artifacts.
 *
 * Primary responsibilities:
 * - Derive Library and interactive workflow actions from artifact type
 * - Keep action descriptors serializable and UI-agnostic
 *
 * Dependencies:
 * - Visual artifact types
 */

import type {
  ThreeDModelFormat,
  VisualArtifact,
  VisualArtifactType
} from './types'

export const visualArtifactWorkflowActionIds = [
  'save-to-library',
  'open-in-library',
  'export-artifact',
  'create-task',
  'create-campaign',
  'create-route',
  'assign-reps',
  'schedule-visits',
  'edit-blueprint',
  'edit-map',
  'edit-model'
] as const

export type VisualArtifactWorkflowActionId =
  (typeof visualArtifactWorkflowActionIds)[number]

export type VisualArtifactWorkflowSurface = 'library' | 'interactive'

export type VisualArtifactWorkflowActionCategory =
  | 'library'
  | 'export'
  | 'create'
  | 'assign'
  | 'edit'

export type VisualArtifactWorkflowActionTarget =
  | 'library'
  | 'artifact-file'
  | 'task'
  | 'campaign'
  | 'route'
  | 'rep-assignment'
  | 'visit-schedule'
  | 'blueprint-editor'
  | 'map-editor'
  | 'model-editor'

export type VisualArtifactWorkflowAction = {
  id: VisualArtifactWorkflowActionId
  label: string
  description: string
  category: VisualArtifactWorkflowActionCategory
  target: VisualArtifactWorkflowActionTarget
  surfaces: readonly VisualArtifactWorkflowSurface[]
  artifactId: string
  artifactTitle: string
  artifactType: VisualArtifactType
  exportFormat?: ThreeDModelFormat
}

type VisualArtifactWorkflowActionTemplate = Omit<
  VisualArtifactWorkflowAction,
  'artifactId' | 'artifactTitle' | 'artifactType'
>

type NonModelVisualArtifactType = Exclude<VisualArtifactType, '3d-model'>

const sharedSurfaces = ['library', 'interactive'] as const
const interactiveSurface = ['interactive'] as const

const saveToLibraryAction: VisualArtifactWorkflowActionTemplate = {
  id: 'save-to-library',
  label: 'Save to Library',
  description: 'Save this artifact to Library.',
  category: 'library',
  target: 'library',
  surfaces: interactiveSurface
}

const openInLibraryAction: VisualArtifactWorkflowActionTemplate = {
  id: 'open-in-library',
  label: 'Open in Library',
  description: 'Open this artifact from Library.',
  category: 'library',
  target: 'library',
  surfaces: sharedSurfaces
}

const createTaskAction: VisualArtifactWorkflowActionTemplate = {
  id: 'create-task',
  label: 'Create task',
  description: 'Create a follow-up task from this artifact.',
  category: 'create',
  target: 'task',
  surfaces: sharedSurfaces
}

const createCampaignAction: VisualArtifactWorkflowActionTemplate = {
  id: 'create-campaign',
  label: 'Create campaign',
  description: 'Create a campaign draft from this artifact.',
  category: 'create',
  target: 'campaign',
  surfaces: sharedSurfaces
}

const createRouteAction: VisualArtifactWorkflowActionTemplate = {
  id: 'create-route',
  label: 'Create route',
  description: 'Create a route from this artifact.',
  category: 'create',
  target: 'route',
  surfaces: sharedSurfaces
}

const assignRepsAction: VisualArtifactWorkflowActionTemplate = {
  id: 'assign-reps',
  label: 'Assign reps',
  description: 'Assign reps from this route or territory.',
  category: 'assign',
  target: 'rep-assignment',
  surfaces: sharedSurfaces
}

const scheduleVisitsAction: VisualArtifactWorkflowActionTemplate = {
  id: 'schedule-visits',
  label: 'Schedule visits',
  description: 'Schedule visits from this route or territory.',
  category: 'create',
  target: 'visit-schedule',
  surfaces: sharedSurfaces
}

const editBlueprintAction: VisualArtifactWorkflowActionTemplate = {
  id: 'edit-blueprint',
  label: 'Edit blueprint',
  description: 'Open this blueprint for edits.',
  category: 'edit',
  target: 'blueprint-editor',
  surfaces: sharedSurfaces
}

const editMapAction: VisualArtifactWorkflowActionTemplate = {
  id: 'edit-map',
  label: 'Edit map',
  description: 'Open this map for edits.',
  category: 'edit',
  target: 'map-editor',
  surfaces: sharedSurfaces
}

const editModelAction: VisualArtifactWorkflowActionTemplate = {
  id: 'edit-model',
  label: 'Edit model',
  description: 'Open this model for edits.',
  category: 'edit',
  target: 'model-editor',
  surfaces: sharedSurfaces
}

const typeSpecificActionsByArtifactType: Record<
  VisualArtifactType,
  readonly VisualArtifactWorkflowActionTemplate[]
> = {
  '3d-model': [createTaskAction, editModelAction],
  schematic: [createTaskAction, createCampaignAction],
  'address-map': [
    createTaskAction,
    createCampaignAction,
    createRouteAction,
    editMapAction
  ],
  heatmap: [createTaskAction, createCampaignAction, editMapAction],
  'route-map': [
    createTaskAction,
    createCampaignAction,
    createRouteAction,
    assignRepsAction,
    scheduleVisitsAction,
    editMapAction
  ],
  'territory-map': [
    createTaskAction,
    createCampaignAction,
    createRouteAction,
    assignRepsAction,
    scheduleVisitsAction,
    editMapAction
  ],
  blueprint: [createTaskAction, editBlueprintAction]
}

// --------------

export function getVisualArtifactWorkflowActions(
  artifact: VisualArtifact
): VisualArtifactWorkflowAction[] {
  return [
    saveToLibraryAction,
    openInLibraryAction,
    getExportAction(artifact),
    ...typeSpecificActionsByArtifactType[artifact.type]
  ].map((action) => buildAction(artifact, action))
}

// --------------

export function getVisualArtifactWorkflowActionsForSurface(
  artifact: VisualArtifact,
  surface: VisualArtifactWorkflowSurface
): VisualArtifactWorkflowAction[] {
  return getVisualArtifactWorkflowActions(artifact).filter((action) =>
    action.surfaces.includes(surface)
  )
}

// --------------

function buildAction(
  artifact: VisualArtifact,
  action: VisualArtifactWorkflowActionTemplate
): VisualArtifactWorkflowAction {
  return {
    ...action,
    artifactId: artifact.id,
    artifactTitle: artifact.title,
    artifactType: artifact.type
  }
}

// --------------

function getExportAction(artifact: VisualArtifact): VisualArtifactWorkflowActionTemplate {
  if (artifact.type === '3d-model') {
    return {
      id: 'export-artifact',
      label: `Export ${artifact.data.format.toUpperCase()}`,
      description: 'Export this model for sharing or download.',
      category: 'export',
      target: 'artifact-file',
      surfaces: sharedSurfaces,
      exportFormat: artifact.data.format
    }
  }

  return {
    id: 'export-artifact',
    label: `Export ${getArtifactNoun(artifact.type)}`,
    description: 'Export this artifact for sharing or download.',
    category: 'export',
    target: 'artifact-file',
    surfaces: sharedSurfaces
  }
}

// --------------

function getArtifactNoun(type: NonModelVisualArtifactType) {
  if (type === 'schematic') return 'schematic'
  if (type === 'address-map') return 'map'
  if (type === 'heatmap') return 'heatmap'
  if (type === 'route-map') return 'route map'
  if (type === 'territory-map') return 'territory map'
  if (type === 'blueprint') return 'blueprint'
  return assertNever(type)
}

// --------------

function assertNever(value: never): never {
  throw new Error(`Unsupported visual artifact type: ${value}`)
}
